/**
 * The cost sheet's pure rules (SA-1197, spec §3): the TypeScript twin of
 * backend/app/custom/compass/cost_sheet.py. shared/cost-sheet-cases.json pins
 * halfUp, rateOf, lineValue and totals in both suites; when a case and the
 * code disagree, the code changes. The leg matcher, the fuel rule and the
 * words have no twin: the server builds the pre-fill (spec C3).
 */
import { COST_SHEET_COLUMNS, COST_SHEET_KINDS } from './contract'
import type { CostSheetRow, CostSheetTotals } from './types'

export const KINDS = COST_SHEET_KINDS
export const MONEY_KINDS: readonly string[] = ['fuel', 'charge', 'overflight']
export const TYPED_SOURCE = 'typed'
export const ROTATION_KEY = 'rotation'
/** A figure at or past this is no number (the Python `MAX_FIGURE`). */
export const MAX_FIGURE = 1e15

/** A finite number below `MAX_FIGURE`; a numeric string or a bool is no number (the Python `number`). */
export function numberOf(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && Math.abs(value) < MAX_FIGURE ? value : null
}

/** Half away from zero through the decimal text, as Python's `Decimal(str(x)).quantize(..., ROUND_HALF_UP)`:
 * `1.005` → 1.01 and `-2.345` → -2.35, where `Math.round(1.005 * 100) / 100` gives 1. */
export function halfUp(value: number, places = 2): number {
  if (!Number.isFinite(value)) return value
  const abs = Math.abs(value)
  // Past 2 ** 52 a double is already a whole number: nothing to round (and the shift below would overflow to NaN).
  if (abs >= 2 ** 52) return value
  const text = String(abs)
  // A figure whose shortest-repr text has no exponent is rounded on that text, as Python's Decimal(str(x)) does;
  // shifting the double through a power of ten drifts, from about 1e13 and in rare cases far below it.
  if (!text.includes('e')) return roundText(text, places, value < 0)
  const shifted = abs * 10 ** places
  const rounded = Number(`${Math.round(shifted)}e-${places}`)
  return value < 0 && rounded !== 0 ? -rounded : rounded
}

/** Half-up on the decimal text of a non-negative double: keep `places` fraction digits, add one in the last
 * kept place when the next digit is 5 or more (BigInt carries through the integer digits). */
function roundText(text: string, places: number, negative: boolean): number {
  const [whole, fraction = ''] = text.split('.')
  const kept = (fraction + '0'.repeat(places)).slice(0, places)
  let digits = BigInt(whole + kept)
  if ((fraction[places] ?? '0') >= '5') digits += 1n
  const padded = digits.toString().padStart(places + 1, '0')
  const rounded = Number(places ? `${padded.slice(0, -places)}.${padded.slice(-places)}` : padded)
  return negative && rounded !== 0 ? -rounded : rounded
}

/** An offer's rate as a line's USD rate per US gallon: the offer plus the surcharge, times the
 * leg's factor (0 or none reads 1), to four decimals. */
export function rateOf(offerRate: number, surcharge?: number | null, factor?: number | null): number {
  return halfUp((offerRate + (surcharge ?? 0)) * (factor ? factor : 1), 4)
}

/** A fuel line's quantity × rate, to the cent, or null when either is missing. */
export function fuelValue(row: Partial<CostSheetRow>): number | null {
  const quantity = numberOf(row.quantity), rate = numberOf(row.rate)
  if (quantity === null || rate === null) return null
  return halfUp(quantity * rate)
}

/** A fuel line with a quantity and a rate is worth their product; every other line its value. */
export function lineValue(row: Partial<CostSheetRow>): number | null {
  if (row.kind === 'fuel') {
    const product = fuelValue(row)
    if (product !== null) return product
  }
  return numberOf(row.value)
}

/** The money lines' stored values summed and the block-hours lines', each to the cent. */
export function totals(rows: readonly Partial<CostSheetRow>[]): CostSheetTotals {
  let doc = 0
  let hours = 0
  for (const row of rows) {
    const value = numberOf(row.value)
    if (value === null) continue
    if (row.kind === 'block_hours') hours += value
    else if (MONEY_KINDS.includes(row.kind ?? '')) doc += value
  }
  return { direct_operating_cost: halfUp(doc), block_hours: halfUp(hours) }
}

export function emptyRow(kind: string): CostSheetRow {
  return {
    kind, from: null, to: null, label: null, station: null, quantity: null, rate: null, reference_rate: null,
    value: null, reference_value: null, source: null, as_of: null, note: null,
    visit: null, category: null, source_kind: null, source_ref: null, burn_ref: null,
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

/** The columns that hold text: a stored value of any other type reads as blank. */
const TEXT_COLUMNS: readonly string[] = ['from', 'to', 'label', 'station', 'source', 'as_of', 'note', 'category', 'source_kind', 'source_ref', 'burn_ref']

/** A stop number is kept only as an integer (SA-1209); anything else reads as none. */
function visitOf(value: unknown): number | null {
  return typeof value === 'number' && Number.isInteger(value) ? value : null
}

/** The rows of a stored value, each with the eighteen columns; anything else is no rows. */
export function rowsOf(value: unknown): CostSheetRow[] {
  if (!Array.isArray(value)) return []
  return value.filter(isRecord).map((row) => ({
    ...emptyRow(String(row.kind ?? '')),
    ...Object.fromEntries(COST_SHEET_COLUMNS.filter((id) => id !== 'kind').map((id) => [
      id, id === 'visit' ? visitOf(row[id])
        : TEXT_COLUMNS.includes(id) ? (typeof row[id] === 'string' ? row[id] : null) : (row[id] ?? null),
    ])),
  }) as CostSheetRow)
}

export function legKey(row: { from: string | null; to: string | null }): string {
  return row.from || row.to ? `${row.from ?? ''}>${row.to ?? ''}` : ROTATION_KEY
}

export function sameLeg(a: { from: string | null; to: string | null }, b: { from: string | null; to: string | null }): boolean {
  return (a.from ?? null) === (b.from ?? null) && (a.to ?? null) === (b.to ?? null)
}
